import { useMemo, useState } from "react";
import { addDays, format, parseISO, subDays } from "date-fns";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest, getQueryFn, queryClient } from "@/lib/queryClient";
import { PageHeader } from "@/components/shared/page-header";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { useToast } from "@/hooks/use-toast";
import { Calendar, Pencil } from "lucide-react";

interface StaffProfileRow {
  userId: string;
  name: string;
  role: string;
  profile: {
    weeklyCapacityMinutes: number;
    title: string | null;
    discipline: string | null;
    timezone: string | null;
    colorHex: string | null;
  } | null;
  schedule: Array<{ weekday: number; minutes: number }>;
}

interface CapacityResponse {
  weekStart: string;
  weekEnd: string;
  rows: Array<{
    userId: string;
    userName: string;
    weeklyCapacityMinutes: number;
    bookedMinutes: number;
    availableMinutes: number;
    utilizationPercent: number;
    days: Array<{
      date: string;
      capacityMinutes: number;
      bookedMinutes: number;
      availableMinutes: number;
    }>;
  }>;
}

interface CapacityFormState {
  userId: string;
  weeklyCapacityMinutes: string;
  title: string;
  discipline: string;
  timezone: string;
  colorHex: string;
  schedule: string[];
}

const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

function buildInitialForm(row: StaffProfileRow | null): CapacityFormState {
  const schedule = Array.from({ length: 7 }, () => "0");
  for (const entry of row?.schedule ?? []) {
    schedule[entry.weekday] = String(entry.minutes);
  }

  return {
    userId: row?.userId ?? "",
    weeklyCapacityMinutes: String(row?.profile?.weeklyCapacityMinutes ?? 2400),
    title: row?.profile?.title ?? "",
    discipline: row?.profile?.discipline ?? "",
    timezone: row?.profile?.timezone ?? "Europe/London",
    colorHex: row?.profile?.colorHex ?? "#0f172a",
    schedule,
  };
}

export default function CapacityPage() {
  const { toast } = useToast();
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const weekday = today.getDay();
    const diff = weekday === 0 ? -6 : 1 - weekday;
    today.setDate(today.getDate() + diff);
    return format(today, "yyyy-MM-dd");
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<StaffProfileRow | null>(null);
  const [form, setForm] = useState<CapacityFormState>(buildInitialForm(null));

  const { data: staffProfiles } = useQuery<StaffProfileRow[]>({
    queryKey: ["/api/staff-profiles"],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const { data: capacity } = useQuery<CapacityResponse>({
    queryKey: [`/api/capacity/week?startDate=${weekStart}`],
    queryFn: getQueryFn({ on401: "throw" }),
  });

  const saveProfileMutation = useMutation({
    mutationFn: async (payload: CapacityFormState) => {
      await apiRequest("PATCH", `/api/staff-profiles/${payload.userId}`, {
        userId: payload.userId,
        weeklyCapacityMinutes: Number(payload.weeklyCapacityMinutes),
        title: payload.title || null,
        discipline: payload.discipline || null,
        timezone: payload.timezone || null,
        colorHex: payload.colorHex || null,
      });

      await apiRequest("PUT", `/api/staff-profiles/${payload.userId}/schedule`, {
        entries: payload.schedule.map((minutes, weekday) => ({
          weekday,
          minutes: Number(minutes || "0"),
        })),
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/staff-profiles"] });
      queryClient.invalidateQueries({
        predicate: (query) =>
          typeof query.queryKey[0] === "string" &&
          query.queryKey[0].startsWith("/api/capacity/week"),
      });
      setDialogOpen(false);
      setEditingRow(null);
      toast({ title: "Capacity profile saved" });
    },
    onError: (error: Error) => {
      toast({
        title: "Failed to save capacity profile",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  const summary = useMemo(() => {
    const rows = capacity?.rows ?? [];
    return {
      totalCapacity: rows.reduce((total, row) => total + row.weeklyCapacityMinutes, 0),
      totalBooked: rows.reduce((total, row) => total + row.bookedMinutes, 0),
      totalAvailable: rows.reduce((total, row) => total + row.availableMinutes, 0),
    };
  }, [capacity]);

  function openEdit(row: StaffProfileRow) {
    setEditingRow(row);
    setForm(buildInitialForm(row));
    setDialogOpen(true);
  }

  const weekLabel = capacity
    ? `${format(parseISO(capacity.weekStart), "d MMM")} - ${format(parseISO(capacity.weekEnd), "d MMM yyyy")}`
    : weekStart;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Weekly Capacity"
        description="Capacity is computed from staff schedules plus booked work for the selected week."
        action={
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setWeekStart(format(subDays(parseISO(weekStart), 7), "yyyy-MM-dd"))}
            >
              Previous week
            </Button>
            <Button
              variant="outline"
              onClick={() => setWeekStart(format(addDays(parseISO(weekStart), 7), "yyyy-MM-dd"))}
            >
              Next week
            </Button>
          </div>
        }
      />

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Week</CardTitle>
          </CardHeader>
          <CardContent className="flex items-center gap-3">
            <Calendar className="h-4 w-4 text-muted-foreground" />
            <span className="font-medium">{weekLabel}</span>
          </CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Capacity</CardTitle>
          </CardHeader>
          <CardContent>{summary.totalCapacity} min</CardContent>
        </Card>
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Booked / Free</CardTitle>
          </CardHeader>
          <CardContent>
            {summary.totalBooked} min booked · {summary.totalAvailable} min free
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Team overview</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Staff</TableHead>
                <TableHead>Weekly Capacity</TableHead>
                <TableHead>Booked</TableHead>
                <TableHead>Available</TableHead>
                <TableHead>Utilization</TableHead>
                <TableHead>Daily View</TableHead>
                <TableHead />
              </TableRow>
            </TableHeader>
            <TableBody>
              {capacity?.rows.map((row) => {
                const profile =
                  staffProfiles?.find((item) => item.userId === row.userId) ?? {
                    userId: row.userId,
                    name: row.userName,
                    role: "",
                    profile: null,
                    schedule: [],
                  };
                return (
                  <TableRow key={row.userId}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{row.userName}</p>
                        <p className="text-xs text-muted-foreground">
                          {profile?.profile?.discipline || "No discipline set"}
                        </p>
                      </div>
                    </TableCell>
                    <TableCell>{row.weeklyCapacityMinutes} min</TableCell>
                    <TableCell>{row.bookedMinutes} min</TableCell>
                    <TableCell>{row.availableMinutes} min</TableCell>
                    <TableCell>{row.utilizationPercent}%</TableCell>
                    <TableCell className="min-w-[260px]">
                      <div className="grid grid-cols-7 gap-1 text-[11px]">
                        {row.days.map((day) => (
                          <div key={day.date} className="rounded-md border p-1 text-center">
                            <p className="font-medium">{weekdayLabels[parseISO(day.date).getDay()]}</p>
                            <p>{day.bookedMinutes}/{day.capacityMinutes}</p>
                          </div>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="sm" onClick={() => openEdit(profile)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[720px]">
          <DialogHeader>
            <DialogTitle>Edit capacity profile</DialogTitle>
            <DialogDescription>
              Configure weekly capacity defaults and the explicit per-day work schedule used in utilization calculations.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="weekly-capacity">Weekly capacity minutes</Label>
              <Input
                id="weekly-capacity"
                type="number"
                min="0"
                value={form.weeklyCapacityMinutes}
                onChange={(event) =>
                  setForm((current) => ({ ...current, weeklyCapacityMinutes: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity-title">Title</Label>
              <Input
                id="capacity-title"
                value={form.title}
                onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity-discipline">Discipline</Label>
              <Input
                id="capacity-discipline"
                value={form.discipline}
                onChange={(event) =>
                  setForm((current) => ({ ...current, discipline: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="capacity-timezone">Timezone</Label>
              <Input
                id="capacity-timezone"
                value={form.timezone}
                onChange={(event) =>
                  setForm((current) => ({ ...current, timezone: event.target.value }))
                }
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label htmlFor="capacity-color">Color hex</Label>
              <Input
                id="capacity-color"
                value={form.colorHex}
                onChange={(event) =>
                  setForm((current) => ({ ...current, colorHex: event.target.value }))
                }
              />
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-7">
            {form.schedule.map((minutes, weekday) => (
              <div key={weekday} className="space-y-2">
                <Label htmlFor={`weekday-${weekday}`}>{weekdayLabels[weekday]}</Label>
                <Input
                  id={`weekday-${weekday}`}
                  type="number"
                  min="0"
                  max="1440"
                  value={minutes}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      schedule: current.schedule.map((entry, index) =>
                        index === weekday ? event.target.value : entry,
                      ),
                    }))
                  }
                />
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={() => saveProfileMutation.mutate(form)}
              disabled={!form.userId || saveProfileMutation.isPending}
            >
              Save capacity profile
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
